import { Option } from 'baseui/select';
import LoadingComponent from 'components/common/loading';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetHotelRoom from 'hooks/use-get-hotel-room';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewHotel } from '../../hooks/use-get-default-values';
import {
  VisaDocumentHotelItemFormType,
  VisaDocumentFormType,
} from '../visa-document-form-type';

interface Props {
  parentIndex: number;
}

function useGetVisaHotelTableList(
  parentIndex: number = 0,
  hotelRoomList: Option[],
) {
  const { editable } = useFormState();
  const { t } = useTranslation();

  const columns = React.useMemo<
    IColumn<VisaDocumentHotelItemFormType>[]
  >(() => {
    return [
      {
        header: t('common:room_type'),
        accessorKey: 'hotelRoomId',
        minSize: 180,
        dataType: ColumnDataType.select,
        options: hotelRoomList,
        required: true,
      },
      {
        header: t('common:city_tax'),
        accessorKey: 'cityTax',
        minSize: 100,
        dataType: ColumnDataType.number,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 250,
        dataType: ColumnDataType.text,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [editable, hotelRoomList, t]);

  return columns;
}

export default function VisaHotelTable(props: Props) {
  const { editable } = useFormState();
  const [isShow, setIsShow] = React.useState(true);

  const name = `visaDocumentHotels.${props.parentIndex}.items` as const;

  const { control, trigger, getValues } =
    useFormContext<VisaDocumentFormType>();

  const { fields, update, append, remove } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const { hotelId, hotelRooms, setHotelRooms, isFetching } = useGetHotelRoom({
    name: `visaDocumentHotels.${props.parentIndex}.hotelId`,
  });

  React.useEffect(() => {
    if (!hotelId || hotelId === '') {
      setHotelRooms([]);
      setIsShow(false);
      setTimeout(() => {
        setIsShow(true);
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  React.useEffect(() => {
    if (isFetching) {
      setIsShow(false);
    } else {
      setIsShow(true);
    }
  }, [isFetching]);

  const columns = useGetVisaHotelTableList(props.parentIndex, hotelRooms);

  const dispatch = React.useCallback(
    (action) => {
      const addons = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const addon = addons[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof VisaDocumentHotelItemFormType;
      const value = action.value;

      const otherVal: () => Partial<VisaDocumentHotelItemFormType> = () => {
        return {};
      };

      if (type === 'addRow') {
        append(addNewHotel());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...addon,
          [columnId]: value,
          ...otherVal(),
        });
        trigger(name);
      }
    },
    [append, getValues, name, remove, trigger, update],
  );

  if (isFetching || !isShow) {
    return <LoadingComponent style={{ height: 100, width: '100%' }} />;
  }

  return (
    <TableInput
      columns={columns}
      data={fields}
      dispatch={dispatch}
      disabled={!editable}
      name={name}
      uniqueRowKey="customId"
    />
  );
}
